import React from 'react';
import { useQuery } from '@common/hooks';
import {
  DISPUTE_REVIEW_QUERY,
  DisputeAvailableActionType,
} from '@pay-bill/graphql';

import {
  DocumentPreviewModalDeprecatedProvider,
  Drawer,
  ErrorHandler,
  ErrorMessageTemplate,
  StandardNotFoundErrorMessage,
} from '@common/components';
import { DisputeViewDrawerBody } from './_dispute-view-drawer-body';

export const DisputeViewDrawer = ({
  open,
  onClose,
  disputeCode,
  action,
}: {
  open: boolean;
  onClose: () => void;
  disputeCode?: string;
  action?: DisputeAvailableActionType;
}) => {
  const { data, loading, error } = useQuery(DISPUTE_REVIEW_QUERY, {
    skip: !disputeCode,
    variables: {
      code: disputeCode || '',
    },
  });

  const isDepartmentRestrictedError = (
    error: ReturnType<typeof useQuery>['error']
  ) =>
    error?.graphQLErrors?.some(
      (graphQLError) =>
        graphQLError?.extensions?.code === 'DISPUTE_DEPARTMENT_ACCESS_DENIED'
    ) ?? false;

  if (error) {
    if (isDepartmentRestrictedError(error)) {
      return (
        <Drawer open={open} onClose={onClose} className="dispute-drawer-router">
          <ErrorMessageTemplate
            title="Access limited to your departments"
            imageName="error-no-access"
            imageAlt="Padlock image meaning access denied"
          >
            You can see this dispute in the list, but it belongs to a department
            outside your access. Contact your Works admin if you need access.
          </ErrorMessageTemplate>
        </Drawer>
      );
    }

    return (
      <Drawer open={open} onClose={onClose} className="dispute-drawer-router">
        <ErrorHandler error={error} log="DisputeViewDrawer" />
      </Drawer>
    );
  }

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        className="dispute-drawer-router"
        loading={loading}
      >
        {!disputeCode ? (
          false
        ) : data?.dispute ? (
          <DocumentPreviewModalDeprecatedProvider>
            <DisputeViewDrawerBody
              dispute={data.dispute}
              action={action}
              onClose={onClose}
            />
          </DocumentPreviewModalDeprecatedProvider>
        ) : (
          <StandardNotFoundErrorMessage />
        )}
      </Drawer>
      <style jsx>{`
        :global(.dispute-drawer-router .ant-drawer-header) {
          padding: 0;
          border: none;
        }
        :global(.dispute-drawer-router .ant-drawer-close) {
          position: absolute;
          top: 18px;
          right: 23px;
        }
        :global(.dispute-drawer-router .ant-drawer-body) {
          padding: 0;
          height: 100%;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
      `}</style>
    </>
  );
};
